import { useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { IUserOrganisation } from 'APIPackage/AccountModule/interfaces/IUserOrganisation';
import { IKnoOrganisationChild } from 'APIPackage/CatalogsModule/interfaces/IKnoOrganisationChild';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SelectOrganisations } from 'UIPackage/SelectOrganisationsModule/components/SelectOrganisations';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnoLevelErvk } from 'APIPackage/CatalogsModule/interfaces/IKnoLevelErvk';

import styles from './OrganisationsModal.module.scss';
import {
  userInfoGetKnoAdminLevels,
  userInfoGetOrganisations,
  userInfoGetOrganisationsNextPage,
  userInfoKnoOrganizationsReset,
  userInfoSetIsErvk,
  userInfoSetOrganisationsAdminLevelCode,
  userInfoSetOrganisationsParentId,
} from '../actions/userInfoActions';
import {
  getUserInfoKnoOrganisations,
  getUserInfoOrganisationsAdminLevelCode,
  getUserInfoOrganisationsParentId,
  getUserInfoOrganisationsSearchQuery,
  getUserInfoOrganisationsTotalCount,
} from '../selectors/userInfoSelectors';
import { getCatalogKnoAdminLevelsSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';

interface IOrganisation extends IKnoOrganisationChild {
  name: string;
}

interface IOrganisationsModalProps {
  visible?: boolean;
  selectedOrganisations?: IUserOrganisation[] | IDictionarySelectOption<Partial<IKnoOrganizationDictionary>>[];
  isErvk?: boolean;
  children: ReactNode;
  onApply: (organisations: IKnoOrganisationChild[]) => void;
  onClose: () => void;
}

interface IOrganisationsModalStoreProps {
  adminLevelCode?: string;
  knoOrganizationAdminLevelsSelectOptions: IDictionarySelectOption<IKnoLevelErvk>[];
  organisationId: string | undefined;
  knoOrganisations: IKnoOrganisationChild[];
  searchQuery: string | undefined;
  totalCount: number;
}

const OrganisationsModalWithoutStore = ({
  visible,
  selectedOrganisations,
  children,
  isErvk,
  onApply,
  onClose,
  adminLevelCode,
  knoOrganizationAdminLevelsSelectOptions,
  organisationId,
  knoOrganisations,
  searchQuery,
  totalCount,
}: IOrganisationsModalProps & IOrganisationsModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const currentAdminLevelSelectOption = useMemo(
    () =>
      adminLevelCode
        ? knoOrganizationAdminLevelsSelectOptions.find(item => item.code === adminLevelCode)
        : knoOrganizationAdminLevelsSelectOptions[0],
    [adminLevelCode, knoOrganizationAdminLevelsSelectOptions],
  );

  const getOrganisationKey = useCallback((org: IKnoOrganisationChild): string => {
    return org.guid || `${org.id}-${org.fullName}`;
  }, []);

  const organisationsList = useMemo(() => {
    const uniqueOrganisations = new Map();

    knoOrganisations.forEach(item => {
      const key = getOrganisationKey(item);

      if (!uniqueOrganisations.has(key)) {
        uniqueOrganisations.set(key, {
          ...item,
          name: item.fullName,
          fullName: `${item.fullName}`,
        });
      }
    });

    return Array.from(uniqueOrganisations.values());
  }, [knoOrganisations, getOrganisationKey]);

  useEffect(() => {
    if (isErvk) {
      dispatch(userInfoSetIsErvk(true));
    }
  }, [dispatch, isErvk]);

  useEffect(() => {
    if (visible) {
      dispatch(userInfoGetKnoAdminLevels());
    }
  }, [dispatch, visible]);

  useEffect(() => {
    if (visible && knoOrganizationAdminLevelsSelectOptions[0]) {
      dispatch(userInfoSetOrganisationsAdminLevelCode(knoOrganizationAdminLevelsSelectOptions[0]?.code));
      dispatch(userInfoGetOrganisations());
    }
  }, [dispatch, knoOrganizationAdminLevelsSelectOptions, visible]);

  const [selectedOrganisationsList, setSelectedOrganisationsList] = useState<Array<IOrganisation>>([]);

  useEffect(() => {
    if (selectedOrganisations) {
      const uniqueSelectedOrganisations = new Map();

      selectedOrganisations.forEach(item => {
        const org = {
          ...('guid' in item && { guid: item.guid || item?.dictVersionId || item.value?.dictVersionId }),
          name: item.fullName || '',
          fullName: `${item.fullName}`,
          id: item.id,
          ...('saved' in item && { saved: Boolean(item?.saved) }),
          ...('ervkVersionUuid' in item && { ervkVersionUuid: item.ervkVersionUuid }),
        };

        const key = getOrganisationKey(org);

        if (!uniqueSelectedOrganisations.has(key)) {
          uniqueSelectedOrganisations.set(key, org);
        }
      });

      setSelectedOrganisationsList(Array.from(uniqueSelectedOrganisations.values()));
    }
  }, [selectedOrganisations, getOrganisationKey]);

  const [currentSelectedOrganisationsList, setCurrentSelectedOrganisationsList] = useState<Array<IOrganisation>>([]);

  useEffect(() => {
    setCurrentSelectedOrganisationsList(selectedOrganisationsList);
  }, [selectedOrganisationsList]);

  const handleSaveButtonClick = useCallback(() => {
    onApply(
      currentSelectedOrganisationsList.map(item => ({
        ...item,
        fullName: item.name,
        id: item.id,
        saved: item.saved,
      })),
    );
    onClose();
  }, [currentSelectedOrganisationsList, onApply, onClose]);

  const handleAfterClose = useCallback(() => {
    dispatch(userInfoKnoOrganizationsReset());
    setCurrentSelectedOrganisationsList(selectedOrganisationsList);
  }, [dispatch, selectedOrganisationsList]);

  const handleSelectedOrganisationsChange = useCallback((organisations: IOrganisation[]) => {
    return setCurrentSelectedOrganisationsList(organisations);
  }, []);

  const handleOrganisationsParentIdChange = useCallback(
    (id?: string) => {
      dispatch(userInfoSetOrganisationsParentId(id));
      dispatch(userInfoGetOrganisations());
    },
    [dispatch],
  );

  const handleOrganisationsPageLoad = useCallback(() => {
    if (knoOrganisations.length < totalCount) {
      dispatch(userInfoGetOrganisationsNextPage());
    }
  }, [dispatch, knoOrganisations.length, totalCount]);

  return (
    <Modal visible={visible} modalBodyClassName={styles.ModalBody} onClose={onClose} afterClose={handleAfterClose}>
      <ModalHeader title="Поиск организаций" />
      <ModalBody size="content" border="bottom">
        {children}
        <SelectOrganisations
          isNavigationVisible={!searchQuery?.length}
          isNavigationRoot={!organisationId}
          rootNavigation={currentAdminLevelSelectOption?.label || ''}
          organisationsTitle="Доступные организации"
          selectedOrganisationsTitle="Выбранные организации"
          organisations={organisationsList}
          isOrganisationsExpanding={!searchQuery?.length}
          isMultiple
          selectedOrganisations={currentSelectedOrganisationsList}
          onSelected={handleSelectedOrganisationsChange}
          onOrganisationsParentIdChange={handleOrganisationsParentIdChange}
          onPageLoad={handleOrganisationsPageLoad}
          isLoadButtonVisible={knoOrganisations.length < totalCount}
        />
      </ModalBody>
      <ModalActions>
        <Button text="Закрыть" size="medium" onClick={onClose} />
        <Button text="Сохранить" color="primary" id="saveButton" size="medium" onClick={handleSaveButtonClick} />
      </ModalActions>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IOrganisationsModalStoreProps => ({
  adminLevelCode: getUserInfoOrganisationsAdminLevelCode(state),
  knoOrganizationAdminLevelsSelectOptions: getCatalogKnoAdminLevelsSelectOptions(state),
  organisationId: getUserInfoOrganisationsParentId(state),
  knoOrganisations: getUserInfoKnoOrganisations(state),
  searchQuery: getUserInfoOrganisationsSearchQuery(state),
  totalCount: getUserInfoOrganisationsTotalCount(state),
});

export const OrganisationsModal = connect(mapStateToProps)(OrganisationsModalWithoutStore);
